/**
 * Backup fingerprint для случаев, когда cookie всё-таки урезалась.
 *
 * Использует:
 *   - User-Agent (стабильный кусок)
 *   - Screen resolution
 *   - Timezone offset
 *   - Language
 *   - Color depth
 *   - Hardware concurrency
 *   - Platform
 *   - Memory (если доступно)
 *
 * НЕ использует canvas/audio fingerprinting — это считается tracking
 * со стороны браузеров и блокируется в Safari ITP / Firefox ETP.
 * Текущий вариант — лёгкий «soft» fingerprint, который не нарушает privacy.
 */

const FP_VERSION = 'v1';

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number;
}

export async function computeFingerprint(): Promise<string> {
  if (typeof window === 'undefined') return '';

  const nav = navigator as NavigatorWithMemory;

  const components: string[] = [
    FP_VERSION,
    nav.userAgent || '',
    nav.language || '',
    nav.platform || '',
    String(nav.hardwareConcurrency || 0),
    String(nav.deviceMemory || 0),
    String(screen.width || 0),
    String(screen.height || 0),
    String(screen.colorDepth || 0),
    String(new Date().getTimezoneOffset()),
  ];

  const raw = components.join('|');
  return sha256(raw);
}

async function sha256(input: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buf = new TextEncoder().encode(input);
      const hashBuf = await crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(hashBuf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      // fallback
    }
  }
  // Простой fallback hash — не криптостойкий, но для fingerprint OK
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  }
  return 'fb_' + Math.abs(h).toString(16);
}
